import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { transparentize, position, border, transitions, math, normalize } from 'polished';
import React, { createContext, useContext, forwardRef, useCallback, useState, useEffect, Children, cloneElement } from 'react';
import styled, { css, createGlobalStyle } from 'styled-components';
import { variant, margin, color, flexbox, grid, textAlign, layout, position as position$1, space as space$1, width, height, borders as borders$1, display, boxShadow as boxShadow$1 } from 'styled-system';
import * as borderTokens from '@hover/tokens-border';
import * as colorTokens from '@hover/tokens-color';
import * as deviceTokens from '@hover/tokens-device';
import * as sizeTokens from '@hover/tokens-size';
import * as spaceTokens from '@hover/tokens-space';
import * as typographyTokens from '@hover/tokens-typography';
import { fontAvenirNext, fontOpenSans } from '@hover/tokens-typography';
import * as depthTokens from '@hover/tokens-depth';
import { useSpring, useTransition, animated } from 'react-spring';
import warning from 'tiny-warning';
import { faSpinnerThird, faEllipsisV } from '@fortawesome/pro-duotone-svg-icons';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose(source, excluded);
  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];

  return arr2;
}

function _arrayWithoutHoles(arr) {
  if (Array.isArray(arr)) return _arrayLikeToArray(arr);
}

function _iterableToArray(iter) {
  if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _nonIterableSpread() {
  throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _toConsumableArray(arr) {
  return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableSpread();
}

function _taggedTemplateLiteral(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }

  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);

    if (enumerableOnly) {
      symbols = symbols.filter(function (sym) {
        return Object.getOwnPropertyDescriptor(object, sym).enumerable;
      });
    }

    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

var attribute = 'data-testid';

/**
 * Strips the test ID prop from a given props object
 *
 * @example
 * const props = { testId: 'foo', bar: 'baz' };
 * <Component {...stripTestId(props)} />
 * // Equivalent
 * <Component bar="baz" />
 *
 * @param props - Props object with test ID prop
 *
 * @returns Props object without test ID prop
 */
var stripTestId = function stripTestId(_ref) {
  _ref.testId;
      var props = _objectWithoutProperties(_ref, ["testId"]);

  return props;
};
/**
 * Returns an object containing test ID *attribute* (e.g: `data-testid`)
 *
 * @example
 * const props = { testId: 'foo', bar: 'baz' };
 * <Component {...applyTestId(props)} />
 * // Equivalent
 * <Component data-testid="foo" />
 *
 * @param props - Props object with test ID prop
 *
 * @returns Object with test ID attribute
 */

var applyTestId = function applyTestId(_ref2) {
  var testId = _ref2.testId;
  return _defineProperty({}, attribute, testId);
};
/**
 * Replaces the test ID prop in supplied props object with a test ID *attribute* (e.g: `data-testid`)
 *
 * @example
 * const props = { testId: 'foo', bar: 'baz' };
 * <Component {...withTestId(props)} />
 * // Equivalent
 * <Component data-testid="foo" bar="baz" />
 *
 * @param props - Props object with test ID prop
 *
 * @returns Props object with test ID *attribute* in place of test ID prop
 */

var withTestId = function withTestId(_ref4) {
  var testId = _ref4.testId,
      props = _objectWithoutProperties(_ref4, ["testId"]);

  return _objectSpread2(_objectSpread2({}, applyTestId({
    testId: testId
  })), props);
};

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  var _i = arr && (typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"]);

  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;

  var _s, _e;

  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}

/**
 * Conditionally includes CSS based on the value of a prop
 *
 * @param prop name of prop to check
 * @param styles a styled-components `css` template tag, or a function
 *               that takes a `Theme` and returns a `css` template tag
 */
var cssForProp = function cssForProp(prop, styles) {
  return function (props) {
    if (props[prop]) {
      return typeof styles === 'function' ? styles(props.theme) : styles;
    }

    return '';
  };
};

/**
 * Generates a `box-shadow` CSS property
 *
 * @example
 * boxShadow('#ff0000', 0.75, '1px', '1px', '3px');
 * // '1px 1px 3px rgba(255, 0, 0, 0.75)'
 *
 * @param color - shadow color
 * @param opacity - opacity to be applied to shadow color
 * @param values - remaining shadow values in the same order as CSS spec
 */
var boxShadow = function boxShadow(color, opacity) {
  for (var _len = arguments.length, values = new Array(_len > 2 ? _len - 2 : 0), _key = 2; _key < _len; _key++) {
    values[_key - 2] = arguments[_key];
  }

  return values.concat(transparentize(1 - opacity, color)).join(' ');
};
var shadows = {
  distance500: /*#__PURE__*/boxShadow(depthTokens.distance500Color, depthTokens.distance500Opacity, depthTokens.distance500OffsetX, depthTokens.distance500OffsetY, depthTokens.distance500BlurRadius, depthTokens.distance500SpreadRadius),
  distance600: /*#__PURE__*/boxShadow(depthTokens.distance600Color, depthTokens.distance600Opacity, depthTokens.distance600OffsetX, depthTokens.distance600OffsetY, depthTokens.distance600BlurRadius, depthTokens.distance600SpreadRadius),
  glowPrimary: /*#__PURE__*/boxShadow(depthTokens.glowPrimaryColor, depthTokens.glowPrimaryOpacity, depthTokens.glowPrimaryOffsetX, depthTokens.glowPrimaryOffsetY, depthTokens.glowPrimaryBlurRadius, depthTokens.glowPrimarySpreadRadius),
  glowDanger: /*#__PURE__*/boxShadow(depthTokens.glowDangerColor, depthTokens.glowDangerOpacity, depthTokens.glowDangerOffsetX, depthTokens.glowDangerOffsetY, depthTokens.glowDangerBlurRadius, depthTokens.glowDangerSpreadRadius)
};

var borderStyle = 'solid';
var neutral = {
  borderWidth: borderTokens.width400,
  borderRadius: borderTokens.radius300,
  borderColor: borderTokens.colorNeutral,
  borderStyle: borderStyle
};
var primary = {
  borderWidth: borderTokens.width400,
  borderRadius: borderTokens.radius300,
  borderColor: borderTokens.colorPrimary,
  borderStyle: borderStyle
};
var danger = {
  borderWidth: borderTokens.width400,
  borderRadius: borderTokens.radius300,
  borderColor: borderTokens.colorDanger,
  borderStyle: borderStyle
};
var borderPresets = {
  neutral: neutral,
  primary: primary,
  danger: danger,
  idle: /*#__PURE__*/_objectSpread2( /*#__PURE__*/_objectSpread2({}, neutral), {}, {
    boxShadow: /*#__PURE__*/[boxShadow(borderTokens.colorNeutral, 0, '0px 0px'), boxShadow(borderTokens.colorNeutral, 0, 'inset 0px 0px 0px 1px')].join(',')
  }),
  focus: /*#__PURE__*/_objectSpread2( /*#__PURE__*/_objectSpread2({}, primary), {}, {
    boxShadow: /*#__PURE__*/[shadows.glowPrimary, boxShadow(borderTokens.colorPrimary, 0.5, 'inset 0px 0px 0px 1px')].join(',')
  }),
  invalid: /*#__PURE__*/_objectSpread2( /*#__PURE__*/_objectSpread2({}, danger), {}, {
    boxShadow: /*#__PURE__*/[shadows.glowDanger, boxShadow(borderTokens.colorDanger, 0, 'inset 0px 0px 0px 1px')].join(',')
  }),
  invalidFocus: /*#__PURE__*/_objectSpread2( /*#__PURE__*/_objectSpread2({}, danger), {}, {
    boxShadow: /*#__PURE__*/[shadows.glowDanger, boxShadow(borderTokens.colorDanger, 0.5, 'inset 0px 0px 0px 1px')].join(',')
  })
};

var fromPreset = function fromPreset(preset) {
  return (// eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    "".concat(preset.borderWidth, " solid ").concat(preset.borderColor)
  );
};

var borders = {
  primary: /*#__PURE__*/fromPreset(borderPresets.primary),
  neutral: /*#__PURE__*/fromPreset(borderPresets.neutral),
  danger: /*#__PURE__*/fromPreset(borderPresets.danger)
};

var spread = '2px';

var expand = function expand() {
  var modifier = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '0px';
  return position(math("-1 * ((".concat(borderTokens.width500, " + ").concat(spread, ") + ").concat(modifier, "px)")));
};
/**
 * Applies position properties to focus border in the `:focus` state.  The
 * default focus state expands the border 2px beyond the outside of the element
 * taking into account the width of the focus border itself. This can be
 * customized using the modifier parameter.
 *
 * @param modifier - Number of pixels to add or subtract from expanded distance,
 * for example -1px to contract the border or 1px to expand it further
 *
 * @returns CSS object to be applied to the element receiving focus
 */


var focusDistance = function focusDistance(modifier) {
  return {
    ':focus:before': expand(modifier)
  };
};
/**
 * Applies border radius property to focus border in the `:focus` state. The
 * default focus state inherits the border radius property from the element.
 *
 * @param radius - Border radius to apply
 *
 * @returns CSS object to be applied to the element receiving focus
 */

var focusRadius = function focusRadius(radius) {
  return {
    ':focus:before': {
      borderRadius: radius
    }
  };
};
var focus = {
  position: 'relative',
  ':before': /*#__PURE__*/_objectSpread2( /*#__PURE__*/_objectSpread2( /*#__PURE__*/_objectSpread2({
    position: 'absolute',
    content: "''",
    borderRadius: 'inherit',
    opacity: 0
  }, /*#__PURE__*/position(0)), /*#__PURE__*/border(borderTokens.width500, 'solid', colorTokens.primary400)), /*#__PURE__*/transitions(['top', ' bottom', ' right', ' left', ' opacity', 'border-radius'], // TODO: replace with a token
  '250ms ease-out')),
  ':focus:before': /*#__PURE__*/_objectSpread2({
    opacity: 1
  }, /*#__PURE__*/expand()),
  ':focus': {
    outline: 0
  },
  '::-moz-focus-inner': {
    border: 0
  }
};

var forKeys = function forKeys(keys, css) {
  return keys.map(function (k) {
    return _defineProperty({}, k, css);
  }).reduce(function (acc, value) {
    return _objectSpread2(_objectSpread2({}, acc), value);
  });
};

var shapes = {
  solid: /*#__PURE__*/_objectSpread2( /*#__PURE__*/_objectSpread2({}, /*#__PURE__*/forKeys(['pill', 'circle'], {
    borderRadius: borderTokens.radius900
  })), /*#__PURE__*/forKeys(['box', 'square'], /*#__PURE__*/_objectSpread2({
    borderRadius: borderTokens.radius300
  }, /*#__PURE__*/focusRadius( /*#__PURE__*/math( /*#__PURE__*/"".concat(borderTokens.radius300, " * 2")))))),
  outline: /*#__PURE__*/_objectSpread2( /*#__PURE__*/_objectSpread2({}, /*#__PURE__*/forKeys(['pill', 'circle'], {
    borderRadius: borderTokens.radius900
  })), /*#__PURE__*/forKeys(['box', 'square'], /*#__PURE__*/_objectSpread2({
    borderRadius: borderTokens.radius300
  }, /*#__PURE__*/focusRadius( /*#__PURE__*/math( /*#__PURE__*/"".concat(borderTokens.radius300, " * 2")))))),
  minimal: /*#__PURE__*/_objectSpread2( /*#__PURE__*/_objectSpread2({}, /*#__PURE__*/forKeys(['pill', 'circle'], /*#__PURE__*/_objectSpread2({
    borderRadius: borderTokens.radius900
  }, /*#__PURE__*/focusDistance('-3px')))), /*#__PURE__*/forKeys(['box', 'square'], /*#__PURE__*/_objectSpread2({
    borderRadius: borderTokens.radius300
  }, /*#__PURE__*/focusDistance('-3px'))))
};
var fills = {
  solid: {
    borderStyle: 'none'
  },
  outline: /*#__PURE__*/_objectSpread2({
    borderStyle: 'solid',
    borderWidth: borderTokens.width400,
    borderColor: 'currentcolor',
    backgroundColor: 'transparent'
  }, /*#__PURE__*/focusDistance(borderTokens.width400)),
  minimal: {
    borderStyle: 'none',
    backgroundColor: 'transparent'
  }
};
var sizes$1 = /*#__PURE__*/_objectSpread2( /*#__PURE__*/_objectSpread2({}, /*#__PURE__*/forKeys(['box', 'pill'], {
  "default": {
    paddingLeft: spaceTokens.size500,
    paddingRight: spaceTokens.size500,
    fontSize: typographyTokens.size400,
    lineHeight: typographyTokens.height400,
    fontWeight: typographyTokens.weight700,
    height: sizeTokens.size300
  },
  large: {
    paddingLeft: spaceTokens.size600,
    paddingRight: spaceTokens.size600,
    fontSize: typographyTokens.size400,
    lineHeight: typographyTokens.height400,
    fontWeight: typographyTokens.weight700,
    height: sizeTokens.size400
  }
})), /*#__PURE__*/forKeys(['square', 'circle'], {
  "default": {
    padding: spaceTokens.size0,
    width: sizeTokens.size300,
    height: sizeTokens.size300
  },
  large: {
    padding: spaceTokens.size0,
    width: sizeTokens.size400,
    height: sizeTokens.size400
  }
}));
var colors$1 = {
  solid: {
    primary: {
      color: colorTokens.neutral000,
      backgroundColor: colorTokens.primary600,
      ':hover:not([disabled])': {
        backgroundColor: colorTokens.primary700
      },
      ':active:not([disabled])': {
        backgroundColor: colorTokens.primary800
      }
    },
    danger: {
      color: colorTokens.neutral000,
      backgroundColor: colorTokens.danger500,
      ':hover:not([disabled])': {
        backgroundColor: colorTokens.danger600
      },
      ':active:not([disabled])': {
        backgroundColor: colorTokens.danger700
      }
    },
    success: {
      color: colorTokens.neutral000,
      backgroundColor: colorTokens.success500,
      ':hover:not([disabled])': {
        backgroundColor: colorTokens.success600
      },
      ':active:not([disabled])': {
        backgroundColor: colorTokens.success700
      }
    },
    disabled: {
      backgroundColor: colorTokens.neutral200,
      color: colorTokens.neutral500
    }
  },
  outline: {
    primary: {
      color: colorTokens.primary600,
      boxShadow: /*#__PURE__*/"inset 0px 0px 0px 1px ".concat( /*#__PURE__*/transparentize(1, colorTokens.primary600)),
      ':hover:not([disabled])': {
        color: colorTokens.primary700,
        backgroundColor: colorTokens.primary100,
        boxShadow: /*#__PURE__*/"inset 0px 0px 0px 1px ".concat(colorTokens.primary700)
      },
      ':active:not([disabled])': {
        color: colorTokens.primary800,
        backgroundColor: colorTokens.primary200,
        boxShadow: /*#__PURE__*/"inset 0px 0px 0px 1px ".concat(colorTokens.primary800)
      }
    },
    danger: {
      color: colorTokens.danger500,
      boxShadow: /*#__PURE__*/"inset 0px 0px 0px 1px ".concat( /*#__PURE__*/transparentize(1, colorTokens.danger500)),
      ':hover:not([disabled])': {
        color: colorTokens.danger600,
        backgroundColor: colorTokens.danger100,
        boxShadow: /*#__PURE__*/"inset 0px 0px 0px 1px ".concat(colorTokens.danger600)
      },
      ':active:not([disabled])': {
        color: colorTokens.danger700,
        backgroundColor: colorTokens.danger200,
        boxShadow: /*#__PURE__*/"inset 0px 0px 0px 1px ".concat(colorTokens.danger700)
      }
    },
    success: {
      color: colorTokens.success500,
      boxShadow: /*#__PURE__*/"inset 0px 0px 0px 1px ".concat( /*#__PURE__*/transparentize(1, colorTokens.success500)),
      ':hover:not([disabled])': {
        color: colorTokens.success600,
        backgroundColor: colorTokens.success100,
        boxShadow: /*#__PURE__*/"inset 0px 0px 0px 1px ".concat(colorTokens.success600)
      },
      ':active:not([disabled])': {
        color: colorTokens.success700,
        backgroundColor: colorTokens.success200,
        boxShadow: /*#__PURE__*/"inset 0px 0px 0px 1px ".concat(colorTokens.success700)
      }
    },
    disabled: {
      color: colorTokens.neutral500,
      borderColor: colorTokens.neutral200
    }
  },
  minimal: {
    primary: {
      color: colorTokens.primary600,
      ':hover:not([disabled])': {
        color: colorTokens.primary700,
        backgroundColor: colorTokens.primary100
      },
      ':active:not([disabled])': {
        color: colorTokens.primary800,
        backgroundColor: colorTokens.primary200
      }
    },
    danger: {
      color: colorTokens.danger500,
      ':hover:not([disabled])': {
        color: colorTokens.danger600,
        backgroundColor: colorTokens.danger100
      },
      ':active:not([disabled])': {
        color: colorTokens.danger700,
        backgroundColor: colorTokens.danger200
      }
    },
    success: {
      color: colorTokens.success500,
      ':hover:not([disabled])': {
        color: colorTokens.success600,
        backgroundColor: colorTokens.success100
      },
      ':active:not([disabled])': {
        color: colorTokens.success700,
        backgroundColor: colorTokens.success200
      }
    },
    disabled: {
      color: colorTokens.neutral500,
      borderColor: colorTokens.neutral200
    }
  }
};

var buttonStyles = /*#__PURE__*/Object.freeze({
  __proto__: null,
  shapes: shapes,
  fills: fills,
  sizes: sizes$1,
  colors: colors$1
});

var headingStyles = {
  '200': {
    fontSize: typographyTokens.heading200Size,
    fontWeight: typographyTokens.heading200Weight,
    lineHeight: typographyTokens.heading200Height,
    color: typographyTokens.heading200Color
  },
  '300': {
    fontSize: typographyTokens.heading300Size,
    fontWeight: typographyTokens.heading300Weight,
    lineHeight: typographyTokens.heading300Height,
    color: typographyTokens.heading300Color
  },
  '400': {
    fontSize: typographyTokens.heading400Size,
    fontWeight: typographyTokens.heading400Weight,
    lineHeight: typographyTokens.heading400Height,
    color: typographyTokens.heading400Color
  },
  '500': {
    fontSize: typographyTokens.heading500Size,
    fontWeight: typographyTokens.heading500Weight,
    lineHeight: typographyTokens.heading500Height,
    color: typographyTokens.heading500Color
  },
  '600': {
    fontSize: typographyTokens.heading600Size,
    fontWeight: typographyTokens.heading600Weight,
    lineHeight: typographyTokens.heading600Height,
    color: typographyTokens.heading600Color
  },
  '650': {
    fontSize: typographyTokens.heading650Size,
    fontWeight: typographyTokens.heading650Weight,
    lineHeight: typographyTokens.heading650Height,
    color: typographyTokens.heading600Color
  },
  '700': {
    fontSize: typographyTokens.heading700Size,
    fontWeight: typographyTokens.heading700Weight,
    lineHeight: typographyTokens.heading700Height,
    color: typographyTokens.heading700Color
  }
};

var iconStyles = {
  small: {
    width: sizeTokens.size100,
    height: sizeTokens.size100,
    margin: spaceTokens.size100
  },
  medium: {
    width: sizeTokens.size200,
    height: sizeTokens.size200,
    margin: spaceTokens.size100
  },
  large: {
    width: sizeTokens.size300,
    height: sizeTokens.size300,
    margin: spaceTokens.size200
  },
  huge: {
    width: sizeTokens.size400,
    height: sizeTokens.size400,
    margin: spaceTokens.size300
  }
};

var textStyles = {
  '200': {
    fontSize: typographyTokens.body200Size,
    fontWeight: typographyTokens.body200Weight,
    lineHeight: typographyTokens.body200Height,
    color: typographyTokens.body200Color
  },
  '300': {
    fontSize: typographyTokens.body300Size,
    fontWeight: typographyTokens.body300Weight,
    lineHeight: typographyTokens.body300Height,
    color: typographyTokens.body300Color
  },
  '400': {
    fontSize: typographyTokens.body400Size,
    fontWeight: typographyTokens.body400Weight,
    lineHeight: typographyTokens.body400Height,
    color: typographyTokens.body400Color
  },
  '500': {
    fontSize: typographyTokens.body500Size,
    fontWeight: typographyTokens.body500Weight,
    lineHeight: typographyTokens.body500Height,
    color: typographyTokens.body500Color
  },
  '600': {
    fontSize: typographyTokens.body600Size,
    fontWeight: typographyTokens.body600Weight,
    lineHeight: typographyTokens.body600Height,
    color: typographyTokens.body600Color
  }
};

var colors = /*#__PURE__*/_objectSpread2( /*#__PURE__*/_objectSpread2({}, colorTokens), {}, {
  inherit: 'inherit'
});

var sizes = sizeTokens;
var breakpoints = {
  '100': deviceTokens.breakpointWidth100,
  '200': deviceTokens.breakpointWidth200,
  '300': deviceTokens.breakpointWidth300,
  '400': deviceTokens.breakpointWidth400,
  '500': deviceTokens.breakpointWidth500,
  "default": '',
  desktop: deviceTokens.breakpointWidthDesktop,
  mobile: deviceTokens.breakpointWidthMobile,
  tablet: deviceTokens.breakpointWidthTablet
};
var radii = {
  '300': borderTokens.radius300,
  '900': borderTokens.radius900,
  round: borderTokens.radiusRound
};
var borderWidths = {
  '0': borderTokens.width0,
  '400': borderTokens.width400,
  '500': borderTokens.width500
};
var space = {
  '0': spaceTokens.size0,
  '50': spaceTokens.size50,
  '100': spaceTokens.size100,
  '200': spaceTokens.size200,
  '300': spaceTokens.size300,
  '400': spaceTokens.size400,
  '500': spaceTokens.size500,
  '600': spaceTokens.size600,
  '700': spaceTokens.size700,
  '800': spaceTokens.size800,
  '900': spaceTokens.size900,
  '1000': spaceTokens.size1000,
  '1100': spaceTokens.size1100
};
var fonts = {
  body: typographyTokens.familyBody,
  brand: typographyTokens.familyBrand
};
var fontSizes = {
  '100': typographyTokens.size100,
  '200': typographyTokens.size200,
  '300': typographyTokens.size300,
  '400': typographyTokens.size400,
  '500': typographyTokens.size500,
  '600': typographyTokens.size600,
  '700': typographyTokens.size700,
  '800': typographyTokens.size800,
  '850': typographyTokens.size850,
  '900': typographyTokens.size900
};
var lineHeights = {
  '100': typographyTokens.height100,
  '200': typographyTokens.height200,
  '300': typographyTokens.height300,
  '400': typographyTokens.height400,
  '500': typographyTokens.height500,
  '600': typographyTokens.height600,
  '700': typographyTokens.height700,
  '800': typographyTokens.height800,
  '850': typographyTokens.height850,
  '900': typographyTokens.height900
};
var fontWeights = {
  '100': typographyTokens.weight100,
  '300': typographyTokens.weight300,
  '400': typographyTokens.weight400,
  '500': typographyTokens.weight500,
  '600': typographyTokens.weight600,
  '700': typographyTokens.weight700,
  bold: typographyTokens.bodyBoldWeight
};
var theme = {
  buttonStyles: buttonStyles,
  breakpoints: breakpoints,
  iconStyles: iconStyles,
  colors: colors,
  borderWidths: borderWidths,
  radii: radii,
  sizes: sizes,
  space: space,
  fonts: fonts,
  fontSizes: fontSizes,
  lineHeights: lineHeights,
  fontWeights: fontWeights,
  borders: borders,
  borderPresets: borderPresets,
  focus: focus,
  textStyles: textStyles,
  headingStyles: headingStyles,
  shadows: shadows
};

var ThemeContext = /*#__PURE__*/createContext(theme);

var useTheme = function useTheme() {
  return useContext(ThemeContext);
};

var _templateObject$h, _templateObject2$6;
var Frame = /*#__PURE__*/styled.div(_templateObject$h || (_templateObject$h = /*#__PURE__*/_taggedTemplateLiteral(["\n  ", "\n  ", "\n  position: relative;\n"])), /*#__PURE__*/variant({
  prop: 'size',
  scale: 'iconStyles'
}), margin);
var Icon = /*#__PURE__*/styled(FontAwesomeIcon).withConfig({
  shouldForwardProp: function shouldForwardProp(prop) {
    return ![].concat(_toConsumableArray(color.propNames), _toConsumableArray(margin.propNames), ['theme']).includes(prop);
  }
})(_templateObject2$6 || (_templateObject2$6 = /*#__PURE__*/_taggedTemplateLiteral(["\n  ", "\n  ", "\n  /* TODO: replace with a token */\n  ", "\n  height: 100% !important;\n  width: 100% !important;\n"])), color, /*#__PURE__*/position('absolute', 0), /*#__PURE__*/transitions(['color'], '250ms ease'));
var AwesomeIcon = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var _ref$size = _ref.size,
      size = _ref$size === void 0 ? 'small' : _ref$size,
      props = _objectWithoutProperties(_ref, ["size"]);

  var theme = useTheme();
  return /*#__PURE__*/React.createElement(Frame, _extends({
    size: size,
    theme: theme
  }, withTestId(props)), /*#__PURE__*/React.createElement(Icon, _extends({
    forwardedRef: ref,
    theme: theme
  }, stripTestId(props))));
});

var _templateObject$g;
var Element$9 = /*#__PURE__*/styled.p(_templateObject$g || (_templateObject$g = /*#__PURE__*/_taggedTemplateLiteral(["\n  font-family: ", ";\n  ", "\n  ", "\n  ", "\n  ", "\n  ", "\n  ", "\n"])), function (props) {
  return props.theme.fonts.body;
}, /*#__PURE__*/variant({
  prop: 'size',
  scale: 'textStyles'
}), color, flexbox, grid, margin, textAlign);
var Body = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
      element = _ref.element,
      props = _objectWithoutProperties(_ref, ["children", "element"]);

  return /*#__PURE__*/React.createElement(Element$9, _extends({
    as: element
  }, withTestId(props), {
    ref: ref,
    theme: useTheme()
  }), children);
});

var _templateObject$f;
var Element$8 = /*#__PURE__*/styled.div(_templateObject$f || (_templateObject$f = /*#__PURE__*/_taggedTemplateLiteral(["\n  box-sizing: border-box;\n  display: flex;\n  ", "\n  ", "\n  ", "\n  ", "\n  ", "\n"])), flexbox, grid, layout, position$1, space$1);
var Box = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
      element = _ref.element,
      as = _ref.as,
      props = _objectWithoutProperties(_ref, ["children", "element", "as"]);

  return /*#__PURE__*/React.createElement(Element$8, _extends({
    as: element !== null && element !== void 0 ? element : as,
    theme: useTheme()
  }, withTestId(props), {
    ref: ref
  }), children);
});

var accessibility = function accessibility(component, message) {
  return "<".concat(component, "> [\u267F\uFE0F] ").concat(message);
};

/* eslint-disable @typescript-eslint/no-redeclare */
var Copy = {
  LOADING: 'Loading'
};
var ProgressBarValue = {
  MAX: 100,
  MIN: 0
};

var _templateObject$e;
var Container$3 = /*#__PURE__*/styled.div(_templateObject$e || (_templateObject$e = /*#__PURE__*/_taggedTemplateLiteral(["\n  align-content: center;\n  display: flex;\n  justify-content: center;\n  ", "\n  ", "\n"])), flexbox, grid);
var Loader = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var _ref$label = _ref.label,
      label = _ref$label === void 0 ? Copy.LOADING : _ref$label,
      _ref$color = _ref.color,
      color = _ref$color === void 0 ? 'primary600' : _ref$color,
      size = _ref.size,
      props = _objectWithoutProperties(_ref, ["label", "color", "size"]);

  return /*#__PURE__*/React.createElement(Container$3, _extends({}, withTestId(props), {
    "aria-busy": true,
    "aria-label": label,
    "aria-valuemax": ProgressBarValue.MAX,
    "aria-valuemin": ProgressBarValue.MIN,
    ref: ref,
    role: "progressbar",
    theme: useTheme()
  }), /*#__PURE__*/React.createElement(AwesomeIcon, {
    color: color,
    icon: faSpinnerThird,
    size: size,
    spin: true
  }));
});

var _templateObject$d, _templateObject2$5, _templateObject3$5, _templateObject4$2;

var shapeVariant = function shapeVariant(props) {
  var _props$fill;

  return variant({
    prop: 'shape',
    scale: "buttonStyles.shapes.".concat((_props$fill = props.fill) !== null && _props$fill !== void 0 ? _props$fill : 'solid')
  });
};

var Element$7 = /*#__PURE__*/styled.button(_templateObject$d || (_templateObject$d = /*#__PURE__*/_taggedTemplateLiteral(["\n  font-family: ", ";\n  padding: 0;\n  ", "\n  ", "\n  ", "\n  ", "\n  ", "\n  ", "\n  ", "\n  ", "\n  ", "\n"])), function (props) {
  return props.theme.fonts.body;
}, function (props) {
  return props.theme.focus;
}, function (props) {
  return !props.disabled && {
    cursor: 'pointer'
  };
}, /*#__PURE__*/transitions(['color', 'background', 'border', 'box-shadow'], // TODO: replace with a token
'250ms ease-in-out'), shapeVariant, /*#__PURE__*/variant({
  prop: 'fill',
  scale: 'buttonStyles.fills'
}), function (props) {
  var _props$fill2;

  return variant({
    prop: 'color',
    scale: "buttonStyles.colors.".concat((_props$fill2 = props.fill) !== null && _props$fill2 !== void 0 ? _props$fill2 : 'solid')
  });
}, margin, flexbox, grid);
var Wrapper = /*#__PURE__*/styled.div(_templateObject2$5 || (_templateObject2$5 = /*#__PURE__*/_taggedTemplateLiteral(["\n  align-items: center;\n  display: flex;\n  justify-content: center;\n  overflow: hidden;\n  position: relative;\n  ", "\n"])), shapeVariant);
var StyledLoader = /*#__PURE__*/styled(Loader)(_templateObject3$5 || (_templateObject3$5 = /*#__PURE__*/_taggedTemplateLiteral(["\n  position: absolute;\n"])));
var AnimatedLoader = /*#__PURE__*/animated(StyledLoader);
var StyledContent = /*#__PURE__*/styled.div(_templateObject4$2 || (_templateObject4$2 = /*#__PURE__*/_taggedTemplateLiteral(["\n  align-items: center;\n  display: flex;\n  position: relative;\n  justify-content: center;\n  width: 100%;\n  white-space: nowrap;\n  ", "\n  ", "\n  ", "\n"])), function (props) {
  return variant({
    prop: 'size',
    scale: "buttonStyles.sizes.".concat(props.shape)
  });
}, width, height);
var AnimatedContent = /*#__PURE__*/animated(StyledContent);

var Button = function Button(_ref) {
  var children = _ref.children,
      _ref$color = _ref.color,
      color = _ref$color === void 0 ? 'primary' : _ref$color,
      _ref$disabled = _ref.disabled,
      disabled = _ref$disabled === void 0 ? false : _ref$disabled,
      _ref$fill = _ref.fill,
      fill = _ref$fill === void 0 ? 'solid' : _ref$fill,
      height = _ref.height,
      iconAfter = _ref.iconAfter,
      iconBefore = _ref.iconBefore,
      label = _ref.label,
      _ref$loading = _ref.loading,
      loading = _ref$loading === void 0 ? false : _ref$loading,
      onPress = _ref.onPress,
      _ref$shape = _ref.shape,
      shape = _ref$shape === void 0 ? 'pill' : _ref$shape,
      _ref$size = _ref.size,
      size = _ref$size === void 0 ? 'default' : _ref$size,
      width = _ref.width,
      props = _objectWithoutProperties(_ref, ["children", "color", "disabled", "fill", "height", "iconAfter", "iconBefore", "label", "loading", "onPress", "shape", "size", "width"]);

  var buildInterpolation = function buildInterpolation(opacity, transform) {
    return fill === 'minimal' ? {
      opacity: opacity
    } : {
      left: 'unset',
      opacity: opacity,
      top: 'unset',
      transform: transform
    };
  };

  var above = buildInterpolation(0, 'translate3d(0, -100%, 0)');
  var visible = buildInterpolation(1, 'translate3d(0, 0%, 0)');
  var below = buildInterpolation(0, 'translate3d(0, 100%, 0)'); // TODO: replace with a token

  var config = {
    tension: 200,
    friction: 20,
    mass: 0.5,
    velocity: 2
  };
  var contentStyles = useSpring({
    config: config,
    to: loading ? above : visible
  });
  var loaderTransitions = useTransition(loading, null, {
    config: config,
    from: below,
    enter: visible,
    leave: below
  });
  var onClick = useCallback(function (event) {
    return !disabled && (onPress === null || onPress === void 0 ? void 0 : onPress(event));
  }, [disabled, onPress]);
  var ariaLabel = label !== null && label !== void 0 ? label : typeof children === 'string' ? children : undefined;
  process.env.NODE_ENV !== "production" ? warning(!!ariaLabel, accessibility('Button', 'a label is required when children is not a string')) : void 0;
  var theme = useTheme();
  return /*#__PURE__*/React.createElement(Element$7, _extends({
    "aria-label": ariaLabel,
    color: disabled ? 'disabled' : color,
    disabled: Boolean(disabled || loading),
    fill: fill,
    onClick: onClick,
    shape: shape,
    size: size,
    theme: theme
  }, withTestId(props)), /*#__PURE__*/React.createElement(Wrapper, _extends({}, applyTestId({
    testId: 'wrapper'
  }), {
    fill: fill,
    shape: shape,
    theme: theme
  }), loaderTransitions.map(function (_ref2) {
    var item = _ref2.item,
        key = _ref2.key,
        style = _ref2.props;
    return item && /*#__PURE__*/React.createElement(AnimatedLoader, {
      color: "inherit",
      key: key,
      size: size === 'default' ? 'small' : 'medium',
      style: style
    });
  }), /*#__PURE__*/React.createElement(AnimatedContent, {
    height: height,
    shape: shape,
    size: size,
    style: contentStyles,
    theme: theme,
    width: width
  }, iconBefore && /*#__PURE__*/React.createElement(AwesomeIcon, {
    icon: iconBefore,
    marginLeft: 0,
    marginRight: 200,
    marginY: 0
  }), children, iconAfter && /*#__PURE__*/React.createElement(AwesomeIcon, {
    icon: iconAfter,
    marginLeft: 200,
    marginRight: 0,
    marginY: 0
  }))));
};

var Label = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
      box = _ref.box,
      props = _objectWithoutProperties(_ref, ["children", "box"]);

  return box === true ? /*#__PURE__*/React.createElement(Box, _extends({
    element: "label"
  }, props, {
    ref: ref
  }), children) : /*#__PURE__*/React.createElement(Body, _extends({
    element: "label"
  }, props, {
    ref: ref
  }), children);
});

var _templateObject$c, _templateObject2$4, _templateObject3$4;
var Container$2 = /*#__PURE__*/styled(Box)(_templateObject$c || (_templateObject$c = /*#__PURE__*/_taggedTemplateLiteral(["\n  position: relative;\n  padding-top: ", ";\n  padding-bottom: ", ";\n"])), function (props) {
  return math("".concat(props.theme.lineHeights[500], " + ").concat(props.theme.space[50]));
}, function (props) {
  return math("".concat(props.theme.lineHeights[400], " + ").concat(props.theme.space[50]));
});
var StyledLabel = /*#__PURE__*/styled(Label)(_templateObject2$4 || (_templateObject2$4 = /*#__PURE__*/_taggedTemplateLiteral(["\n  left: 0;\n  position: absolute;\n  top: 0;\n  /* TODO: replace with a token */\n  transition: color 250ms ease;\n"])));
var AnimatedLabel = /*#__PURE__*/animated(StyledLabel);
var ErrorMessage = /*#__PURE__*/animated( /*#__PURE__*/styled(Body)(_templateObject3$4 || (_templateObject3$4 = /*#__PURE__*/_taggedTemplateLiteral(["\n  bottom: 0;\n  left: 0;\n  position: absolute;\n"]))));
var LABEL_SIZE = 400;
var ERROR_MESSAGE_SIZE = 400;
var Field = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var _childProps$disabled;

  var _ref$autohide = _ref.autohide,
      autohide = _ref$autohide === void 0 ? false : _ref$autohide,
      children = _ref.children,
      error = _ref.error,
      label = _ref.label,
      name = _ref.name,
      disabled = _ref.disabled,
      props = _objectWithoutProperties(_ref, ["autohide", "children", "error", "label", "name", "disabled"]);

  var _useState = useState(''),
      _useState2 = _slicedToArray(_useState, 2),
      errorMessage = _useState2[0],
      setErrorMessage = _useState2[1];

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      isFocused = _useState4[0],
      beFocused = _useState4[1];

  var theme = useTheme();
  var hasError = Boolean(error);
  var errorHidden = {
    transform: "translate3d(0, -".concat(theme.lineHeights[ERROR_MESSAGE_SIZE], ", 0)"),
    opacity: 0
  };
  var errorVisible = {
    transform: "translate3d(0, -".concat(theme.space[50], ", 0)"),
    opacity: 1
  };
  var errorTransitions = useTransition(hasError, null, {
    from: errorHidden,
    enter: errorVisible,
    leave: errorHidden
  });
  useEffect(function () {
    if (error) setErrorMessage(error);
  }, [error]); // TODO: figure out how to type this more accurately... do we restrict
  // supported inputs and use `invariant` to enforce `children` here?

  var childProps = React.Children.only(children).props;

  var _useState5 = useState(!autohide || childProps.value !== undefined),
      _useState6 = _slicedToArray(_useState5, 2),
      isLabelShown = _useState6[0],
      setLabelShown = _useState6[1];

  var labelHidden = {
    opacity: 0,
    transform: "translate3d(0, ".concat(theme.lineHeights[LABEL_SIZE], ", 0)")
  };
  var labelVisible = {
    opacity: 1,
    transform: "translate3d(0, 0, 0)"
  };
  var labelTransitions = useTransition(isLabelShown, null, {
    from: labelHidden,
    enter: labelVisible,
    leave: labelHidden
  });

  var onBlurProxy = function onBlurProxy(event) {
    var _childProps$onBlur;

    (_childProps$onBlur = childProps.onBlur) === null || _childProps$onBlur === void 0 ? void 0 : _childProps$onBlur.call(childProps, event);
    beFocused(false);
  };

  var onChangeProxy = function onChangeProxy(event) {
    var _childProps$onChange;

    (_childProps$onChange = childProps.onChange) === null || _childProps$onChange === void 0 ? void 0 : _childProps$onChange.call(childProps, event);
    if (autohide) setLabelShown(Boolean(event.target.value));
  };

  var onFocusProxy = function onFocusProxy(event) {
    var _childProps$onFocus;

    (_childProps$onFocus = childProps.onFocus) === null || _childProps$onFocus === void 0 ? void 0 : _childProps$onFocus.call(childProps, event);
    beFocused(true);
  };

  var inputProps = {
    'aria-invalid': hasError,
    disabled: (_childProps$disabled = childProps.disabled) !== null && _childProps$disabled !== void 0 ? _childProps$disabled : disabled,
    error: hasError,
    id: name,
    label: isLabelShown ? undefined : label,
    name: name,
    onBlur: onBlurProxy,
    onChange: onChangeProxy,
    onFocus: onFocusProxy
  };

  var labelColor = function labelColor() {
    if (hasError) return 'danger500';
    if (isFocused) return 'primary600';
    return 'neutral600';
  };

  return /*#__PURE__*/React.createElement(Container$2, _extends({
    ref: ref
  }, withTestId(props), {
    flexDirection: "column",
    theme: theme
  }), autohide ? labelTransitions.map(function (_ref2) {
    var item = _ref2.item,
        key = _ref2.key,
        style = _ref2.props;
    return item && /*#__PURE__*/React.createElement(AnimatedLabel, {
      color: labelColor(),
      htmlFor: name,
      key: key,
      size: 500,
      style: style
    }, label);
  }) : /*#__PURE__*/React.createElement(StyledLabel, {
    color: labelColor(),
    htmlFor: name,
    size: 500
  }, label), errorTransitions.map(function (_ref3) {
    var item = _ref3.item,
        key = _ref3.key,
        style = _ref3.props;
    return item && /*#__PURE__*/React.createElement(ErrorMessage, {
      "aria-live": "polite",
      color: "danger500",
      element: "span",
      key: key,
      role: "alert",
      size: ERROR_MESSAGE_SIZE,
      style: style
    }, errorMessage);
  }), Children.map(children, function (child) {
    return /*#__PURE__*/cloneElement(child, inputProps);
  }));
});

var ASSET_HOST = 'https://assets.hover.to/fonts/blueprint/';
var hostFromProps = function hostFromProps(_ref) {
  var _ref$host = _ref.host,
      host = _ref$host === void 0 ? ASSET_HOST : _ref$host;
  return host;
};

var _templateObject$b;
var AvenirNext = /*#__PURE__*/css(_templateObject$b || (_templateObject$b = /*#__PURE__*/_taggedTemplateLiteral(["\n  @font-face {\n    font-family: ", ";\n    src: url(", "avenir-next--regular.ttf) format('truetype');\n    font-weight: 400;\n    font-style: normal;\n    font-display: fallback;\n  }\n\n  @font-face {\n    font-family: ", ";\n    src: url(", "avenir-next--italic.ttf) format('truetype');\n    font-weight: 400;\n    font-style: italic;\n    font-display: fallback;\n  }\n\n  @font-face {\n    font-family: ", ";\n    src: url(", "avenir-next--medium.ttf) format('truetype');\n    font-weight: 500;\n    font-style: normal;\n    font-display: fallback;\n  }\n\n  @font-face {\n    font-family: ", ";\n    src: url(", "avenir-next--medium-italic.ttf) format('truetype');\n    font-weight: 500;\n    font-style: italic;\n    font-display: fallback;\n  }\n\n  @font-face {\n    font-family: ", ";\n    src: url(", "avenir-next--demi.ttf) format('truetype');\n    font-weight: 600;\n    font-style: normal;\n    font-display: fallback;\n  }\n\n  @font-face {\n    font-family: ", ";\n    src: url(", "avenir-next--demi-italic.ttf) format('truetype');\n    font-weight: 600;\n    font-style: italic;\n    font-display: fallback;\n  }\n\n  @font-face {\n    font-family: ", ";\n    src: url(", "avenir-next--bold.ttf) format('truetype');\n    font-weight: 700;\n    font-style: normal;\n    font-display: fallback;\n  }\n\n  @font-face {\n    font-family: ", ";\n    src: url(", "avenir-next--bold-italic.ttf) format('truetype');\n    font-weight: 700;\n    font-style: italic;\n    font-display: fallback;\n  }\n"])), fontAvenirNext, hostFromProps, fontAvenirNext, hostFromProps, fontAvenirNext, hostFromProps, fontAvenirNext, hostFromProps, fontAvenirNext, hostFromProps, fontAvenirNext, hostFromProps, fontAvenirNext, hostFromProps, fontAvenirNext, hostFromProps);

var _templateObject$a;
var OpenSans = /*#__PURE__*/css(_templateObject$a || (_templateObject$a = /*#__PURE__*/_taggedTemplateLiteral(["\n  @font-face {\n    font-family: ", ";\n    src: url(", "open-sans--light.ttf) format('truetype');\n    font-weight: 100;\n    font-style: normal;\n    font-display: fallback;\n  }\n\n  @font-face {\n    font-family: ", ";\n    src: url(", "open-sans--light-italic.ttf) format('truetype');\n    font-weight: 100;\n    font-style: italic;\n    font-display: fallback;\n  }\n\n  @font-face {\n    font-family: ", ";\n    src: url(", "open-sans--light.ttf) format('truetype');\n    font-weight: 300;\n    font-style: normal;\n    font-display: fallback;\n  }\n\n  @font-face {\n    font-family: ", ";\n    src: url(", "open-sans--light-italic.ttf) format('truetype');\n    font-weight: 300;\n    font-style: italic;\n    font-display: fallback;\n  }\n\n  @font-face {\n    font-family: ", ";\n    src: url(", "open-sans--regular.ttf) format('truetype');\n    font-weight: 400;\n    font-style: normal;\n    font-display: fallback;\n  }\n\n  @font-face {\n    font-family: ", ";\n    src: url(", "open-sans--italic.ttf) format('truetype');\n    font-weight: 400;\n    font-style: italic;\n    font-display: fallback;\n  }\n\n  @font-face {\n    font-family: ", ";\n    src: url(", "open-sans--semi-bold.ttf) format('truetype');\n    font-weight: 500;\n    font-style: normal;\n    font-display: fallback;\n  }\n\n  @font-face {\n    font-family: ", ";\n    src: url(", "open-sans--semi-bold-italic.ttf) format('truetype');\n    font-weight: 500;\n    font-style: italic;\n    font-display: fallback;\n  }\n\n  @font-face {\n    font-family: ", ";\n    src: url(", "open-sans--bold.ttf) format('truetype');\n    font-weight: 700;\n    font-style: normal;\n    font-display: fallback;\n  }\n\n  @font-face {\n    font-family: ", ";\n    src: url(", "open-sans--bold-italic.ttf) format('truetype');\n    font-weight: 700;\n    font-style: italic;\n    font-display: fallback;\n  }\n\n  @font-face {\n    font-family: ", ";\n    src: url(", "open-sans--extra-bold.ttf) format('truetype');\n    font-weight: 800;\n    font-style: normal;\n    font-display: fallback;\n  }\n\n  @font-face {\n    font-family: ", ";\n    src: url(", "open-sans--extra-bold-italic.ttf)\n      format('truetype');\n    font-weight: 800;\n    font-display: fallback;\n    font-style: italic;\n  }\n"])), fontOpenSans, hostFromProps, fontOpenSans, hostFromProps, fontOpenSans, hostFromProps, fontOpenSans, hostFromProps, fontOpenSans, hostFromProps, fontOpenSans, hostFromProps, fontOpenSans, hostFromProps, fontOpenSans, hostFromProps, fontOpenSans, hostFromProps, fontOpenSans, hostFromProps, fontOpenSans, hostFromProps, fontOpenSans, hostFromProps);

var _templateObject$9;
var Fonts = /*#__PURE__*/createGlobalStyle(_templateObject$9 || (_templateObject$9 = /*#__PURE__*/_taggedTemplateLiteral(["\n  ", "\n  ", "\n"])), AvenirNext, OpenSans);

var Form = function Form(_ref) {
  var children = _ref.children,
      props = _objectWithoutProperties(_ref, ["children"]);

  return /*#__PURE__*/React.createElement(Box, _extends({
    as: "form"
  }, props), children);
};

var _templateObject$8;
var Element$6 = /*#__PURE__*/styled.h1(_templateObject$8 || (_templateObject$8 = /*#__PURE__*/_taggedTemplateLiteral(["\n  font-family: ", ";\n  margin-top: 0;\n  ", "\n  ", "\n  ", "\n  ", "\n  ", "\n  ", "\n"])), function (props) {
  return props.theme.fonts.brand;
}, /*#__PURE__*/variant({
  prop: 'size',
  scale: 'headingStyles'
}), color, flexbox, grid, margin, textAlign);

var Heading = function Heading(_ref) {
  var children = _ref.children,
      props = _objectWithoutProperties(_ref, ["children"]);

  return /*#__PURE__*/React.createElement(Element$6, _extends({
    theme: useTheme()
  }, withTestId(props)), children);
};

var _templateObject$7;
var Element$5 = /*#__PURE__*/styled.img(_templateObject$7 || (_templateObject$7 = /*#__PURE__*/_taggedTemplateLiteral(["\n  box-sizing: border-box;\n  ", "\n  ", "\n  ", "\n  ", "\n  ", "\n"])), borders$1, flexbox, grid, function (_ref) {
  var $width = _ref.$width,
      $height = _ref.$height,
      props = _objectWithoutProperties(_ref, ["$width", "$height"]);

  return (// eslint-disable-next-line @typescript-eslint/no-unsafe-return
    layout(_objectSpread2(_objectSpread2({}, props), {}, {
      width: $width,
      height: $height
    }))
  );
}, space$1);
var Image = /*#__PURE__*/forwardRef(function (_ref2, ref) {
  _ref2.children;
      var height = _ref2.height,
      width = _ref2.width,
      props = _objectWithoutProperties(_ref2, ["children", "height", "width"]);

  return /*#__PURE__*/React.createElement(Element$5, _extends({
    $height: height,
    $width: width,
    theme: useTheme()
  }, withTestId(props), {
    ref: ref
  }));
});

var _templateObject$6;
var Element$4 = /*#__PURE__*/styled.a(_templateObject$6 || (_templateObject$6 = /*#__PURE__*/_taggedTemplateLiteral(["\n  /* TODO: replace with a token */\n  transition: color 250ms ease-in-out;\n  font-family: ", ";\n  ", "\n  color: ", ";\n  font-weight: ", ";\n  text-decoration: none;\n  cursor: pointer;\n  ", "\n  ", "\n  ", "\n  ", "\n  ", "\n\n  &:hover {\n    color: ", ";\n  }\n\n  &:active {\n    color: ", ";\n  }\n\n  &:hover,\n  &:active {\n    text-decoration: underline;\n  }\n"])), function (props) {
  return props.theme.fonts.body;
}, /*#__PURE__*/variant({
  prop: 'size',
  scale: 'textStyles'
}), function (props) {
  return props.theme.colors.primary600;
}, function (props) {
  return props.theme.fontWeights[500];
}, display, flexbox, grid, margin, textAlign, function (props) {
  return props.theme.colors.primary700;
}, function (props) {
  return props.theme.colors.primary800;
});
var Link = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
      disabled = _ref.disabled,
      label = _ref.label,
      onPress = _ref.onPress,
      props = _objectWithoutProperties(_ref, ["children", "disabled", "label", "onPress"]);

  var onClick = useCallback(function (event) {
    return !disabled && (onPress === null || onPress === void 0 ? void 0 : onPress(event));
  }, [disabled, onPress]);
  return /*#__PURE__*/React.createElement(Element$4, _extends({
    "aria-label": label
  }, withTestId(props), {
    onClick: onClick,
    ref: ref,
    theme: useTheme()
  }), children);
});

var _templateObject$5;
var Element$3 = /*#__PURE__*/styled(Box)(_templateObject$5 || (_templateObject$5 = /*#__PURE__*/_taggedTemplateLiteral(["\n  ", "\n  ", "\n  ", "\n"])), color, borders$1, boxShadow$1);
var Panel = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
      props = _objectWithoutProperties(_ref, ["children"]);

  return /*#__PURE__*/React.createElement(Element$3, _extends({
    backgroundColor: "background",
    borderRadius: 300,
    flexDirection: "column",
    padding: 300
  }, props, {
    ref: ref,
    theme: useTheme()
  }), children);
});

var _templateObject$4, _templateObject2$3, _templateObject3$3;
var GlobalStyle = /*#__PURE__*/createGlobalStyle(_templateObject$4 || (_templateObject$4 = /*#__PURE__*/_taggedTemplateLiteral(["\n  ", "\n\n  ", "\n\n  ", "\n"])), /*#__PURE__*/cssForProp('colors', function (_ref) {
  var colors = _ref.colors;
  return css(_templateObject2$3 || (_templateObject2$3 = _taggedTemplateLiteral(["\n      body {\n        background-color: ", ";\n        color: ", ";\n      }\n    "])), colors.background, colors.foreground);
}), /*#__PURE__*/cssForProp('fonts', function (_ref2) {
  var fonts = _ref2.fonts;
  return css(_templateObject3$3 || (_templateObject3$3 = _taggedTemplateLiteral(["\n      body {\n        font-family: ", ";\n      }\n\n      h1,\n      h2,\n      h3,\n      h4,\n      h5,\n      h6 {\n        font-family: ", ";\n      }\n    "])), fonts.body, fonts.brand);
}), /*#__PURE__*/cssForProp('normalize', /*#__PURE__*/normalize()));

var Styles = function Styles(_ref3) {
  var _ref3$colors = _ref3.colors,
      colors = _ref3$colors === void 0 ? true : _ref3$colors,
      _ref3$fonts = _ref3.fonts,
      fonts = _ref3$fonts === void 0 ? true : _ref3$fonts,
      _ref3$normalize = _ref3.normalize,
      normalize = _ref3$normalize === void 0 ? true : _ref3$normalize;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(GlobalStyle, {
    colors: colors,
    fonts: fonts,
    normalize: normalize,
    theme: useTheme()
  }));
};

/* eslint-disable @typescript-eslint/no-redeclare */
var TextInputType = {
  text: 'text',
  search: 'search',
  tel: 'tel',
  url: 'url',
  email: 'email',
  number: 'number',
  password: 'password',
  date: 'date',
  time: 'time',
  dateTime: 'datetime-local',
  month: 'month'
};
var TextInputMode = {
  text: 'text',
  search: 'search',
  tel: 'tel',
  url: 'url',
  email: 'email',
  numeric: 'numeric',
  decimal: 'decimal',
  none: 'none'
};

var _templateObject$3, _templateObject2$2, _templateObject3$2;
var Container$1 = /*#__PURE__*/styled.div(_templateObject$3 || (_templateObject$3 = /*#__PURE__*/_taggedTemplateLiteral(["\n  display: inline-block;\n  position: relative;\n  ", "\n  ", "\n  ", "\n"])), margin, flexbox, layout);
var Element$2 = /*#__PURE__*/styled.input(_templateObject2$2 || (_templateObject2$2 = /*#__PURE__*/_taggedTemplateLiteral(["\n  box-sizing: border-box;\n  width: 100%;\n  outline: none;\n  ", "\n"])), function (_ref) {
  var theme = _ref.theme,
      disabled = _ref.disabled,
      error = _ref.error,
      iconBefore = _ref.iconBefore,
      iconAfter = _ref.iconAfter;
  return _objectSpread2(_objectSpread2(_objectSpread2(_objectSpread2(_objectSpread2({
    fontFamily: theme.fonts.body
  }, theme.textStyles[500]), {}, {
    height: theme.sizes.size400,
    paddingRight: iconAfter ? theme.space[700] : theme.space[400],
    paddingLeft: iconBefore ? theme.space[700] : theme.space[400]
  }, transitions(['border', 'box-shadow', 'color'], // TODO: replace with a token
  '250ms ease')), theme.borderPresets.idle), {}, {
    ':focus': theme.borderPresets.focus
  }, error && _objectSpread2(_objectSpread2({}, theme.borderPresets.invalid), {}, {
    ':focus': theme.borderPresets.invalidFocus
  })), {}, {
    '::placeholder': {
      color: theme.colors.neutral500
    }
  }, disabled && {
    color: theme.colors.neutral500,
    backgroundColor: theme.colors.neutral100,
    borderColor: theme.colors.neutral200
  });
});
var IconContainer$1 = /*#__PURE__*/styled.div(_templateObject3$2 || (_templateObject3$2 = /*#__PURE__*/_taggedTemplateLiteral(["\n  position: absolute;\n  top: 0;\n  bottom: 0;\n  display: flex;\n  align-items: center;\n  ", "\n"])), function (_ref2) {
  var position = _ref2.position,
      theme = _ref2.theme;
  return _defineProperty({}, position, theme.space[400]);
});
var TextInput = /*#__PURE__*/forwardRef(function (_ref4, ref) {
  var _ref4$type = _ref4.type,
      type = _ref4$type === void 0 ? TextInputType.text : _ref4$type,
      _ref4$mode = _ref4.mode,
      mode = _ref4$mode === void 0 ? TextInputMode.text : _ref4$mode,
      id = _ref4.id,
      onFocus = _ref4.onFocus,
      onBlur = _ref4.onBlur,
      width = _ref4.width,
      label = _ref4.label,
      name = _ref4.name,
      props = _objectWithoutProperties(_ref4, ["type", "mode", "id", "onFocus", "onBlur", "width", "label", "name"]);

  var theme = useTheme();

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      focused = _useState2[0],
      setFocused = _useState2[1];

  var iconBefore = props.iconBefore,
      iconAfter = props.iconAfter,
      error = props.error;

  var onElementFocus = function onElementFocus(event) {
    setFocused(true);
    onFocus === null || onFocus === void 0 ? void 0 : onFocus(event);
  };

  var onElementBlur = function onElementBlur(event) {
    setFocused(false);
    onBlur === null || onBlur === void 0 ? void 0 : onBlur(event);
  };

  var renderIcon = useCallback(function (icon, position) {
    var color = focused ? 'secondary600' : 'neutral500';
    var errorColor = focused ? 'danger700' : 'danger500';
    return /*#__PURE__*/React.createElement(IconContainer$1, {
      position: position,
      theme: theme
    }, /*#__PURE__*/React.createElement(AwesomeIcon, {
      color: error ? errorColor : color,
      icon: icon,
      margin: 0
    }));
  }, [error, focused, theme]);
  return /*#__PURE__*/React.createElement(Container$1, _extends({
    theme: theme,
    width: width
  }, withTestId(props)), iconBefore && renderIcon(iconBefore, 'left'), /*#__PURE__*/React.createElement(Element$2, _extends({
    "aria-label": label,
    id: id,
    inputMode: mode,
    name: name,
    onBlur: onElementBlur,
    onFocus: onElementFocus,
    ref: ref,
    theme: theme,
    type: type
  }, props)), iconAfter && renderIcon(iconAfter, 'right'));
});

var _templateObject$2, _templateObject2$1, _templateObject3$1, _templateObject4$1;
var Container = /*#__PURE__*/styled.div(_templateObject$2 || (_templateObject$2 = /*#__PURE__*/_taggedTemplateLiteral(["\n  box-sizing: content-box;\n  position: relative;\n  ", ";\n"])), function (_ref) {
  var theme = _ref.theme,
      size = _ref.size;
  var widths = {
    small: theme.sizes.size300,
    "default": theme.sizes.size400
  };
  return {
    padding: theme.space[100],
    width: widths[size],
    borderRadius: theme.radii[900]
  };
});
var Underlay = /*#__PURE__*/styled.div(_templateObject2$1 || (_templateObject2$1 = /*#__PURE__*/_taggedTemplateLiteral(["\n  position: absolute;\n  top: 0;\n  left: 0;\n  width: 100%;\n  height: 100%;\n  ", ";\n"])), function (_ref2) {
  var theme = _ref2.theme;
  return _objectSpread2({
    borderRadius: theme.radii[900],
    backgroundColor: theme.colors.neutral400
  }, transitions(['background-color'], '150ms ease-in-out'));
});
var Thumb = /*#__PURE__*/styled.div(_templateObject3$1 || (_templateObject3$1 = /*#__PURE__*/_taggedTemplateLiteral(["\n  ", ";\n  transform: translateX(0);\n"])), function (_ref3) {
  var theme = _ref3.theme,
      size = _ref3.size;
  var sizes = {
    small: theme.sizes.size100,
    "default": theme.sizes.size200
  };
  return _objectSpread2({
    width: sizes[size],
    height: sizes[size],
    borderRadius: theme.radii[900],
    backgroundColor: theme.colors.neutral000
  }, transitions(['transform'], '150ms ease-in-out'));
});
var Input = /*#__PURE__*/styled.input(_templateObject4$1 || (_templateObject4$1 = /*#__PURE__*/_taggedTemplateLiteral(["\n  cursor: pointer;\n  z-index: 1;\n  appearance: none;\n  border: none;\n  top: 0;\n  left: 0;\n  width: 100%;\n  height: 100%;\n  ", ";\n  position: absolute;\n  :checked ~ ", " {\n    transform: translateX(100%);\n  }\n  :checked ~ ", " {\n    background-color: ", ";\n  }\n  :disabled {\n    cursor: default;\n  }\n\n  :disabled ~ ", " {\n    background-color: ", ";\n  }\n"])), function (_ref4) {
  var theme = _ref4.theme;
  return _objectSpread2({
    borderRadius: theme.radii[900],
    margin: theme.space[0]
  }, theme.focus);
}, Thumb, Underlay, function (_ref5) {
  var theme = _ref5.theme;
  return theme.colors.primary600;
}, Underlay, function (_ref6) {
  var theme = _ref6.theme,
      checked = _ref6.checked;
  return checked ? theme.colors.primary300 : theme.colors.neutral300;
});

var Toggle = function Toggle(_ref7) {
  var value = _ref7.value,
      defaultValue = _ref7.defaultValue,
      onChange = _ref7.onChange,
      _ref7$size = _ref7.size,
      size = _ref7$size === void 0 ? 'default' : _ref7$size,
      id = _ref7.id,
      props = _objectWithoutProperties(_ref7, ["value", "defaultValue", "onChange", "size", "id"]);

  var theme = useTheme();
  return /*#__PURE__*/React.createElement(Container, _extends({
    size: size,
    theme: theme
  }, withTestId(props)), /*#__PURE__*/React.createElement(Input, _extends({
    checked: value,
    defaultChecked: defaultValue,
    id: id,
    onChange: onChange,
    theme: theme,
    type: "checkbox"
  }, stripTestId(props))), /*#__PURE__*/React.createElement(Underlay, {
    "data-testid": "underlay",
    theme: theme
  }), /*#__PURE__*/React.createElement(Thumb, {
    "data-testid": "thumb",
    size: size,
    theme: theme
  }));
};

var _templateObject$1;
var Element$1 = /*#__PURE__*/styled.div(_templateObject$1 || (_templateObject$1 = /*#__PURE__*/_taggedTemplateLiteral(["\n  position: relative;\n  display: flex;\n  justify-content: space-between;\n  flex-direction: ", ";\n  padding: ", ";\n  background: ", ";\n  border-radius: ", ";\n  border: 2px solid\n    ", ";\n  transition-property: border, background;\n  transition-duration: 300ms;\n  cursor: pointer;\n"])), function (_ref) {
  var _ref$flexDirection = _ref.flexDirection,
      flexDirection = _ref$flexDirection === void 0 ? 'column' : _ref$flexDirection;
  return flexDirection === 'column' ? 'column' : 'row';
}, function (_ref2) {
  var theme = _ref2.theme;
  return theme.space[400];
}, function (_ref3) {
  var selected = _ref3.selected,
      theme = _ref3.theme;
  return selected ? theme.colors.primary200 : theme.colors.neutral000;
}, function (_ref4) {
  var theme = _ref4.theme;
  return theme.radii[300];
}, function (_ref5) {
  var selected = _ref5.selected,
      theme = _ref5.theme;
  return selected ? theme.colors.primary500 : theme.colors.neutral200;
});

var BlockOption = function BlockOption(props) {
  var content = props.content,
      flexDirection = props.flexDirection,
      heading = props.heading,
      onClick = props.onClick,
      selected = props.selected,
      subHeading = props.subHeading;

  var _useState = useState(selected || false),
      _useState2 = _slicedToArray(_useState, 2),
      isSelected = _useState2[0],
      setIsSelected = _useState2[1];

  var onClickBlockOption = useCallback(function () {
    setIsSelected(!isSelected);
    onClick === null || onClick === void 0 ? void 0 : onClick(!isSelected);
  }, [isSelected, onClick]);
  return /*#__PURE__*/React.createElement(Element$1, _extends({
    role: "option",
    theme: useTheme()
  }, props, {
    "aria-selected": isSelected,
    flexDirection: flexDirection,
    onClick: onClickBlockOption,
    selected: isSelected
  }), /*#__PURE__*/React.createElement(Box, {
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 0,
    width: 1
  }, /*#__PURE__*/React.createElement(Heading, {
    "data-testid": "option-heading",
    margin: 0,
    size: 200
  }, heading), subHeading && flexDirection === 'column' && /*#__PURE__*/React.createElement(Heading, {
    color: "neutral600",
    "data-testid": "option-subHeading",
    margin: 0,
    size: 200
  }, subHeading)), content && /*#__PURE__*/React.createElement(Body, {
    color: "neutral400",
    "data-testid": "option-content",
    marginBottom: 0,
    marginTop: 0,
    size: 400
  }, content));
};

var _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5, _templateObject6, _templateObject7, _templateObject8, _templateObject9, _templateObject10;
var trackHeight = 8;
var thumbDiameter = 20;
var thumbHoverStyles = /*#__PURE__*/css(_templateObject || (_templateObject = /*#__PURE__*/_taggedTemplateLiteral(["\n  background: ", ";\n"])), function (props) {
  return props.theme.colors.primary700;
});
var thumbFocusStyles = /*#__PURE__*/css(_templateObject2 || (_templateObject2 = /*#__PURE__*/_taggedTemplateLiteral(["\n  box-shadow: 0 0 0 2px ", ";\n  &:hover {\n    box-shadow: 0 0 0 2px ", ";\n  }\n"])), function (props) {
  return props.theme.colors.primaryBase;
}, function (props) {
  return props.theme.colors.primary700;
}); // TODO: replace transition-* values with motion tokens

var thumbStyles = /*#__PURE__*/css(_templateObject3 || (_templateObject3 = /*#__PURE__*/_taggedTemplateLiteral(["\n  appearance: none;\n  border: none;\n  width: ", "px;\n  height: ", "px;\n  background: ", ";\n  border-radius: ", ";\n  box-shadow: ", ";\n  transition-property: background width height box-shadow;\n  transition-duration: 0.15s;\n  transition-timing-function: ease-in-out;\n\n  ", "\n"])), thumbDiameter, thumbDiameter, function (_ref) {
  var theme = _ref.theme,
      disabled = _ref.disabled;
  return disabled ? theme.colors.neutral400 : theme.colors.primaryBase;
}, function (props) {
  return props.theme.radii[900];
}, function (props) {
  return props.theme.shadows.distance500;
}, function (props) {
  return !props.disabled && css(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n      cursor: pointer;\n      &:hover {\n        ", "\n      }\n    "])), thumbHoverStyles);
});

var applyThumbStyles = function applyThumbStyles(styles) {
  return css(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n  &::-webkit-slider-thumb {\n    ", "\n  }\n\n  &::-moz-range-thumb {\n    ", "\n  }\n\n  &::-ms-thumb {\n    ", "\n  }\n"])), styles, styles, styles);
};

var Element = /*#__PURE__*/styled.input(_templateObject6 || (_templateObject6 = /*#__PURE__*/_taggedTemplateLiteral(["\n  appearance: none;\n  outline: none;\n  width: 100%;\n  height: ", "px;\n  background: ", ";\n  border-radius: ", ";\n\n  &::-moz-focus-outer {\n    border: none;\n  }\n\n  ", "\n\n  &:focus {\n    outline: none;\n\n    ", "\n  }\n"])), trackHeight, function (_ref2) {
  var theme = _ref2.theme,
      disabled = _ref2.disabled;
  return disabled ? theme.colors.neutral300 : theme.colors.primary300;
}, function (props) {
  return props.theme.radii[900];
}, /*#__PURE__*/applyThumbStyles(thumbStyles), /*#__PURE__*/applyThumbStyles(thumbFocusStyles));
var RelativeContainer = /*#__PURE__*/styled.div(_templateObject7 || (_templateObject7 = /*#__PURE__*/_taggedTemplateLiteral(["\n  line-height: initial;\n  position: relative;\n  width: 100%;\n"])));
var AbsoluteContainer = /*#__PURE__*/styled.div(_templateObject8 || (_templateObject8 = /*#__PURE__*/_taggedTemplateLiteral(["\n  position: absolute;\n  top: 0;\n  left: 0;\n  width: calc(100% - ", "px);\n  /* marginX needs to match native input margin */\n  margin: 0 2px;\n"])), thumbDiameter);
var IconContainer = /*#__PURE__*/styled.div(_templateObject9 || (_templateObject9 = /*#__PURE__*/_taggedTemplateLiteral(["\n  position: absolute;\n  left: ", ";\n  height: ", "px;\n  width: ", "px;\n  font-size: ", "px;\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  pointer-events: none;\n  z-index: ", ";\n"])), function (_ref3) {
  var left = _ref3.left;
  return "".concat(left !== null && left !== void 0 ? left : 0, "%");
}, thumbDiameter, thumbDiameter, thumbDiameter, function (props) {
  return props.disabled ? -1 : 1;
});
var SuggestedValueIcon = /*#__PURE__*/styled(FontAwesomeIcon)(_templateObject10 || (_templateObject10 = /*#__PURE__*/_taggedTemplateLiteral(["\n  margin: 0;\n  color: ", ";\n"])), function (props) {
  return props.theme.colors.primary900;
});

var Slider = /*#__PURE__*/forwardRef(function (props, ref) {
  var _props$initialValue = props.initialValue,
      initialValue = _props$initialValue === void 0 ? 0 : _props$initialValue,
      _props$step = props.step,
      step = _props$step === void 0 ? 1 : _props$step,
      _props$min = props.min,
      min = _props$min === void 0 ? 0 : _props$min,
      _props$max = props.max,
      max = _props$max === void 0 ? 100 : _props$max,
      disabled = props.disabled,
      onChange = props.onChange,
      suggestedValue = props.suggestedValue;

  var _useState = useState(initialValue),
      _useState2 = _slicedToArray(_useState, 2),
      value = _useState2[0],
      setValue = _useState2[1];

  var setValueAndTriggerChange = useCallback(function (newValue) {
    setValue(newValue);
    onChange === null || onChange === void 0 ? void 0 : onChange(newValue);
  }, [onChange]);
  var onChangeSlider = useCallback(function (event) {
    var newValue = Number(event.currentTarget.value);
    setValueAndTriggerChange(newValue);
  }, [setValueAndTriggerChange]);

  if (disabled && suggestedValue && value !== suggestedValue) {
    setValueAndTriggerChange(suggestedValue);
  }

  var left = 100 * ((suggestedValue !== null && suggestedValue !== void 0 ? suggestedValue : 0) - min) / (max - min);
  var theme = useTheme();
  return /*#__PURE__*/React.createElement(RelativeContainer, {
    theme: theme
  }, /*#__PURE__*/React.createElement(Element, {
    "aria-valuemax": max,
    "aria-valuemin": min,
    "aria-valuenow": value,
    disabled: disabled,
    max: max,
    min: min,
    onChange: onChangeSlider,
    ref: ref,
    role: "slider",
    step: step,
    theme: theme,
    type: "range",
    value: value
  }), suggestedValue !== undefined && /*#__PURE__*/React.createElement(AbsoluteContainer, null, /*#__PURE__*/React.createElement(IconContainer, {
    "data-testid": "icon-container",
    disabled: disabled,
    left: left
  }, /*#__PURE__*/React.createElement(SuggestedValueIcon, {
    icon: faEllipsisV,
    swapOpacity: true,
    theme: theme
  }))));
});

export { AwesomeIcon, BlockOption, Body, Box, Button, Field, Fonts, Form, Heading, Image, Label, Link, Loader, Panel, Slider, Styles, TextInput, Toggle, theme as defaultTheme, useTheme };
